import { useMemo } from 'react'
import { Users } from 'lucide-react'
import Panel from '@/components/dashboards/Panel'
import { BasisNote } from '@/components/dashboards/KnownFigures'
import PivotTable, { emptyMeasureColumns, type PivotColumn } from '@/components/analytics/explorer/PivotTable'
import { useDealFacts, usePeople, usePivot } from '@/components/analytics/explorer/analyticsQueries'
import {
  MEASURE_BY_KEY,
  ROLE_DIMENSION,
  ROLE_LABEL,
  describeFilters,
  totalsOf,
  type AnalyticsFilters,
  type DealFact,
  type PeopleRole,
  type PivotRow,
} from '@/services/dashboardAnalytics'
import { personHref } from './TeamWorkload'

const LEADERBOARD_COLUMNS: PivotColumn[] = [
  { key: 'count' },
  { key: 'commitment' },
  { key: 'fundedCount' },
  { key: 'funded', className: 'hidden lg:table-cell' },
  { key: 'quoteRatePct' },
  { key: 'wtdLtvLtc', className: 'hidden 2xl:table-cell' },
  { key: 'avgDaysToFund', className: 'hidden xl:table-cell' },
]

function shortDate(value: string | null | undefined): string {
  if (!value) return '--'
  const at = Date.parse(value)
  if (!Number.isFinite(at)) return '--'
  return new Date(at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })
}

interface LeaderboardProps {
  role?: PeopleRole
  cohort?: AnalyticsFilters
  title?: string
  className?: string
  /** Rows the host already holds (the CEO board's `leaderboards` panel, cut
   *  to the same cohort) — skips a second pivot request. */
  served?: PivotRow[] | null
}

/**
 * People ranked over the cohort: deals, commitment, funded, quote rate (the
 * desk's hit rate, T-71) and time to fund. Each name opens that person's
 * profile; each number opens exactly its deals. People the roster names
 * with no deal in the cohort are listed with zeros, never dropped (T-54).
 */
export default function Leaderboard({
  role = 'underwriter',
  cohort = {},
  title,
  className,
  served,
}: LeaderboardProps) {
  const dimension = ROLE_DIMENSION[role] ?? 'responsibleParty'
  const pivot = usePivot(served ? null : { dimension, by: 'outcome', filters: cohort })
  const people = usePeople(role)
  const facts = useDealFacts(cohort)

  const factMap = useMemo(() => {
    const map = new Map<string, DealFact>()
    for (const fact of facts.data?.deals ?? []) map.set(fact.id, fact)
    return map
  }, [facts.data])

  const lastActivity = useMemo(() => {
    const map = new Map<string, string | null>()
    for (const person of people.data?.people ?? []) map.set(person.name, person.lastActivityAt)
    return map
  }, [people.data])

  const rows = useMemo<PivotRow[]>(() => {
    const base = served ?? pivot.data?.rows ?? []
    const named = new Set(base.map((row) => row.key))
    // Roster people with nothing in this cohort still belong on the board.
    const zeros: PivotRow[] = (people.data?.people ?? [])
      .filter((person) => !named.has(person.name))
      .map((person) => ({
        key: person.name,
        label: person.name,
        measures: {
          count: 0,
          requested: null,
          commitment: null,
          funded: null,
          activeCount: 0,
          signedCount: 0,
          fundedCount: 0,
          deadCount: 0,
          conversionPct: null,
          wtdLtvLtc: null,
          ltvCoverage: null,
          wtdCouponPct: null,
          couponCoverage: null,
          avgDaysToFund: null,
          avgDaysToFundN: null,
          dealIds: [],
        },
      }))
    return [...base, ...zeros]
  }, [served, pivot.data, people.data])

  const hidden = emptyMeasureColumns(rows, served ? null : totalsOf(pivot.data), LEADERBOARD_COLUMNS)
  const scope = describeFilters(cohort)
  const basis = people.data?.basis

  return (
    <Panel
      title={title ?? `${ROLE_LABEL[role]} leaderboard`}
      kicker={<span title={basis ?? undefined}>{scope}</span>}
      isLoading={!served && pivot.isLoading}
      error={served ? null : pivot.error}
      onRetry={() => void pivot.refetch()}
      isEmpty={!(!served && pivot.isLoading) && rows.length === 0}
      emptyIcon={Users}
      emptyTitle={`No ${ROLE_LABEL[role].toLowerCase()} on record`}
      emptyDescription="No live deal in this cohort names one."
      className={className}
      bodyClassName="!p-0"
      scroll
      footer={
        <BasisNote full={basis}>
          Quote rate = deals that reached a quote or later ÷ deals submitted.
          {hidden.length > 0 && ` Not shown, no value on any row: ${hidden.map((column) => MEASURE_BY_KEY[column.key].label).join(', ')}.`}
        </BasisNote>
      }
    >
      <PivotTable
        rows={rows}
        totals={served ? null : totalsOf(pivot.data)}
        dimensionLabel={ROLE_LABEL[role]}
        by="outcome"
        facts={factMap}
        scopeLabel={scope}
        columns={LEADERBOARD_COLUMNS}
        hideEmptyNote
        labelHref={(row) => (row.key ? personHref(row.key, role) : null)}
        showRank
        extraColumns={[
          {
            key: 'last',
            label: 'Last activity',
            className: 'hidden min-[1800px]:table-cell',
            render: (row) => shortDate(row.key ? lastActivity.get(row.key) : null),
          },
        ]}
      />
    </Panel>
  )
}
